import { useNavigate } from 'react-router-dom'

import { ActionItem, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { convertHoursToTimeFormat } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import DateRange from 'modules/timeoff/components/DateRange'
import styles from 'modules/timeoff/components/Leaves/LeavesList/Section/Item/index.module.css'
import {
  leaveIsHourly,
  leaveTotalDaysOrHours,
  leaveUnits,
} from 'modules/timeoff/lib/leaveEmployees'
import type LeaveModel from 'modules/timeoff/store/models/Leave'

interface Props {
  leave: LeaveModel
  employeeId: number
  employeeTimeoffPolicyId: number | null | undefined
}

const LeaveItem = ({ leave, employeeId, employeeTimeoffPolicyId }: Props) => {
  const { navigation } = useNavigation()
  const { employee: currentEmployee } = useCurrentSession()

  const navigate = useNavigate()

  const onClickEdit = () => {
    const myRoute = navigation.timeOff.leavesEditLeaveId.fullPath({ leave_id: leave.attributes.id })

    const anotherRoute = navigation.employees.id.timeOff.leavesEditLeaveId.fullPath({
      id: employeeId,
      leave_id: leave.id,
    })

    navigate(currentEmployee?.id === employeeId ? myRoute : anotherRoute)
  }

  const halfDayCopy = (): string => {
    const firstHalf = leave.isHalfDayBeginningOfDay()

    return firstHalf
      ? i18n.t('time_off.half_day_item.beggining_of_day')
      : i18n.t('time_off.half_day_item.end_of_day')
  }

  const getYears = () => {
    const years = Object.keys(leaveUnits(leave, employeeTimeoffPolicyId) ?? {})

    if (!years.length) return
    if (years.length < 2) return years[0]

    const yearEnd = years[years.length - 1]?.slice(2)

    return `${years[0]}/${yearEnd}`
  }

  const years = getYears()

  const getNumberOfUnits = () => {
    const total = leaveTotalDaysOrHours(leave, employeeTimeoffPolicyId)

    if (
      !leaveIsHourly(leave, employeeTimeoffPolicyId) &&
      leave.attributes.half_day &&
      total === 0.5
    ) {
      return halfDayCopy()
    }

    const copy = leaveIsHourly(leave, employeeTimeoffPolicyId)
      ? buildCopyLeaveInHours(total)
      : `${i18n.tp('time_off.days_with_years', {
          count: total,
        })} (${years})`

    return copy
  }

  const buildCopyLeaveInHours = (total: number) => {
    return convertHoursToTimeFormat(total, true)
  }

  const frenchCalendarFlag = () => {
    return leave.frenchCalendarFlagEnabled() && leave.isPending()
  }

  const title = leave.leaveType?.name
  const isOpen = !leave.attributes.finish_on

  return (
    <ActionItem onClick={onClickEdit} showIcon={false}>
      <div className={styles.leave}>
        <DateRange
          leave={leave}
          disabled={isOpen}
          employeeTimeoffPolicyId={employeeTimeoffPolicyId}
        />
        <div className={styles.body}>
          {title && (
            <div className={styles.title}>
              <Text size="300" weight="medium" color="grey1000" ellipsis>
                {title}
              </Text>
            </div>
          )}
          {years && (
            <div className={styles.description}>
              <Text.Normal>{!frenchCalendarFlag() && getNumberOfUnits()}</Text.Normal>
            </div>
          )}
        </div>
      </div>
    </ActionItem>
  )
}

export default observer(LeaveItem)
